import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { TextInput } from '@/components/text-input'
import { acceptInvite, type InviteLookup, lookupInvite, PlatformApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Zod schema on the URL search params. TanStack Router validates
 * and types them for us — `token` is required and has to be a
 * non-empty string. Missing / empty → we render an error state
 * rather than calling the backend with garbage.
 */
export const Route = createFileRoute('/accept-invite')({
  component: AcceptInvitePage,
  validateSearch: (search: Record<string, unknown>): { token: string } => {
    const token = typeof search.token === 'string' ? search.token : ''
    return { token }
  },
})

/**
 * Accept-invite landing page.
 *
 * The flow:
 *   1. Read `?token=xyz` from the URL.
 *   2. On mount, call the unauthenticated `lookupInvite` endpoint
 *      to resolve the invite's display metadata (email, tenant
 *      name, inviter). This is what renders in the header — we
 *      want the invitee to know WHO invited them and to WHICH
 *      workspace before they commit to an account.
 *   3. Render a signup form with the email pre-filled AND
 *      read-only (the invite is bound to that exact address).
 *   4. On submit, call `acceptInvite` which:
 *        - Creates the platform_users row via better-auth
 *        - Inserts the tenant_members row
 *        - Marks the invite accepted
 *        - Attaches a valid platform_session cookie to the response
 *   5. Navigate to /account. Because the session cookie is already
 *      set, the account page loads authenticated on first render
 *      — no sign-in round trip.
 *
 * Error states we render explicitly instead of a generic toast:
 *   - Missing token    → "This link is incomplete."
 *   - 404 InviteNotFound → "This invite link is not valid."
 *   - 410 InviteExpired  → "This invite expired on {date}. Ask for a new one."
 *   - 410 InviteConsumed → "This invite was already used. Sign in instead."
 */
function AcceptInvitePage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; invite: InviteLookup }
    | {
        status: 'error'
        message: string
        kind: 'missing' | 'not_found' | 'expired' | 'consumed' | 'other'
      }
  >({ status: 'loading' })

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Stash navigate in a ref so the mount effect can call it without
  // taking a dep on its identity. Same pattern as /account.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    if (!token) {
      setState({
        status: 'error',
        kind: 'missing',
        message: 'This invite link is incomplete — the token is missing. Ask for a new invite.',
      })
      return
    }

    const ctrl = new AbortController()
    lookupInvite(token, ctrl.signal)
      .then((invite) => setState({ status: 'loaded', invite }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (err instanceof PlatformApiError) {
          // The backend returns specific error codes; map them to
          // kind so the render path can tailor the call-to-action.
          const body = err.body as { error?: string } | null
          const errorCode = body?.error ?? ''
          if (err.status === 404 || errorCode === 'InviteNotFound') {
            setState({
              status: 'error',
              kind: 'not_found',
              message: 'This invite link is not valid. Ask for a new one.',
            })
            return
          }
          if (errorCode === 'InviteExpired') {
            setState({
              status: 'error',
              kind: 'expired',
              message: err.message,
            })
            return
          }
          if (errorCode === 'InviteConsumed') {
            setState({
              status: 'error',
              kind: 'consumed',
              message: err.message,
            })
            return
          }
        }
        const message =
          err instanceof PlatformApiError && err.message
            ? err.message
            : 'Failed to load invite. Try again in a moment.'
        setState({ status: 'error', kind: 'other', message })
      })
    return () => ctrl.abort()
  }, [token])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (state.status !== 'loaded') return
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.')
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      await acceptInvite({
        token,
        password,
        name: name.trim() || state.invite.email,
      })
      // Session cookie is already set by the backend response —
      // /account's /me call will succeed on first load.
      void navigateRef.current({ to: '/account' })
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : 'Failed to accept invite. Try again.'
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <NavBar />
      <main>
        {state.status === 'loading' ? (
          <AuthCard title="Loading invite…" subtitle="Fetching invite details">
            <div className="flex justify-center py-6 text-sm text-muted-foreground">
              One moment…
            </div>
          </AuthCard>
        ) : null}

        {state.status === 'error' ? (
          <AuthCard
            title="Invite unavailable"
            subtitle={state.message}
            footer={
              <span>
                Already have an account?{' '}
                <Link to="/sign-in" className="font-medium text-primary hover:underline">
                  Sign in
                </Link>
              </span>
            }
          >
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {state.kind === 'missing'
                ? 'Your link is missing the token parameter. Check the email you received for the full URL.'
                : state.kind === 'expired'
                  ? 'Invites expire after 7 days. Ask a workspace member to send you a fresh one.'
                  : state.kind === 'consumed'
                    ? "This invite was already used — if you're the person who accepted it, just sign in."
                    : 'Double-check the link or ask for a new invite.'}
            </div>
          </AuthCard>
        ) : null}

        {state.status === 'loaded' ? (
          <AuthCard
            title={`Join ${state.invite.tenantName}`}
            subtitle={
              state.invite.invitedByEmail
                ? `${state.invite.invitedByEmail} invited you to join their workspace. Create an account to accept.`
                : 'Someone invited you to join their workspace. Create an account to accept.'
            }
            footer={
              <span>
                Already have an account?{' '}
                <Link to="/sign-in" className="font-medium text-primary hover:underline">
                  Sign in
                </Link>
              </span>
            }
          >
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <TextInput
                label="Your name"
                type="text"
                autoComplete="name"
                placeholder="Alex Doe"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <TextInput
                label="Email"
                type="email"
                autoComplete="email"
                value={state.invite.email}
                readOnly
                // The invite is bound to this exact email on the
                // server, so the UI can't let the user change it
                // without invalidating the invite. Visual disabled
                // style confirms the intent.
                onChange={() => {}}
                className="cursor-not-allowed opacity-80"
              />
              <TextInput
                label="Password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                hint="At least 8 characters."
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />

              {submitError ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {submitError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  'w-full rounded-lg px-4 py-2.5 text-sm font-medium',
                  'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                  'hover:opacity-90 transition-opacity',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                )}
              >
                {submitting ? 'Creating account…' : 'Accept & create account'}
              </button>
            </form>
          </AuthCard>
        ) : null}
      </main>
      <Footer />
    </>
  )
}
